import { type JSX, type ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import { Heading1 } from '@/components/shared/typography/Heading';
import { Description } from '@/components/shared/typography/HelpText';
import { OrganizationSwitcher } from './OrganizationSwitcher';

interface PageHeaderProps {
  children?: ReactNode;
  title: string;
  icon?: LucideIcon;
  subheader?: string;
}

/**
 * PageHeader - Top bar for main content area (appears to the right of sidebar)
 * Supports optional icon and subheader for consistent screen headers
 */
export function PageHeader({
  children,
  title,
  icon: Icon,
  subheader,
}: PageHeaderProps): JSX.Element {
  return (
    <div className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background px-6 min-w-[320px]">
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
          <Heading1 className="truncate">{title}</Heading1>
        </div>
        {subheader && (
          <Description className="truncate">{subheader}</Description>
        )}
      </div>
      <div className="flex items-center gap-3 ml-auto flex-shrink-0">
        {children}
        <div className="min-w-0 max-w-[280px]">
          <OrganizationSwitcher />
        </div>
      </div>
    </div>
  );
}
